import type {
  AvatarContextualChatHistoryTurn,
  AvatarContextualChatSeed,
  AvatarLiveSessionKind,
  AvatarSessionContext
} from './types'
import { PROGRESSIVE_STAGE2_MIN_BUFFERED_MS } from '../core/utils/progressiveLipSync'

export interface LiveBlendshapeFrame {
  time: number
  values: Record<string, number>
}

export interface LivePrompt {
  nodeId?: string
  text?: string
  listening?: boolean
  nodeType?: string
  nodeTitle?: string
  options?: Array<{ label: string; value: string }>
  visual?: Record<string, unknown>
}

type LiveClientState = 'connecting' | 'linked' | 'disconnected' | 'error'

/**
 * Why a dispatch is happening (progressive lip-sync, SDK 2.12.0). Absent on
 * the CamlinVoice / whole-utterance path, whose dispatches are unchanged.
 *
 *  - `stage: 'chunk0'`   first chunk of a streaming turn — start the mouth now
 *  - `stage: 'stage2'`   one superset re-dispatch while the stream is open
 *  - `stage: 'final'`    the complete turn; `deferToPause` asks the consumer
 *                        to flush it at the next speech pause instead of
 *                        rebuilding the clip mid-word
 */
export interface LiveBlendshapeDispatchMeta {
  stage: 'chunk0' | 'stage2' | 'final'
  final: boolean
  deferToPause?: boolean
}

export interface LiveModeClientConfig {
  runtimeUrl: string
  handoffToken: string
  /**
   * Progressive viseme dispatch (2.12.0). ONLY set this on the browser-only
   * chat path (audio played by the in-page <audio> element). Never for
   * CamlinVoice-driven surfaces — there the phone/RTP audio waits for
   * `visual.animation.ready`, so whole-utterance dispatch is correct and the
   * `animation.ready -> playback.countdown` handshake must keep running.
   */
  progressiveDispatch?: boolean
  onBlendshapes?: (frames: LiveBlendshapeFrame[], meta?: LiveBlendshapeDispatchMeta) => void
  onPlaybackCountdown?: (delayMs: number, frames: LiveBlendshapeFrame[]) => void
  onAudioPosition?: (position: { currentTime: number; duration: number; isPlaying: boolean; timestamp?: number }) => void
  onPrompt?: (prompt: LivePrompt) => void
  onListening?: (active: boolean) => void
  onLinked?: (sessionId: string, diagramId?: string, avatarModelId?: string, avatarConfig?: Record<string, unknown>) => void
  onStateChange?: (state: LiveClientState) => void
  /**
   * `terminal: true` means the client has given up for good (reconnect
   * attempts exhausted) — the session is unrecoverable and consumers
   * should flip their UI to an ended state. Errors without the flag may
   * still be followed by a successful automatic reconnect.
   */
  onError?: (error: string, terminal?: boolean) => void
  onTranscript?: (text: string, isFinal: boolean) => void
  onThinking?: (thinking: boolean) => void
  onSentiment?: (sentiment: { label: string; score: number }) => void
  /**
   * Sentiment-decline escalation signal. The runtime fires this on
   * `visual.escalation` when the rolling sentiment-history monitor
   * (sprint 20.5) detects a declining trend and the configured threshold
   * is crossed. Hosts use it to switch UI mode ("Connecting you to a
   * person…"), route to a human agent, or surface a banner. Was missing
   * from canonical until restored to match the old VisualChannelClient.
   */
  onEscalation?: (escalation: { triggered: boolean; reason: string; trend: string }) => void
  /**
   * Fires when the runtime advances the flow to a new node. Used by
   * debug overlays, analytics hooks, and per-node UI. Restored to match
   * the old VisualChannelClient — runtime still emits `visual.node_changed`
   * (apps/runtime-service/src/server.ts) so dropping it on the floor was a
   * silent telemetry gap.
   */
  onNodeChanged?: (nodeId: string, nodeType: string, nodeTitle: string) => void
  onSwitchAvatar?: (avatarModelId: string, voiceId?: string, transitionMessage?: string) => void
  onSpeaking?: (speaking: boolean) => void
  onCallEnded?: (reason: string) => void
  /**
   * Slice M4h-6d-4 (May 12 2026) — Visual IVR identity gate UI affordance.
   *
   * Fires when the runtime hits a node tagged
   * `requiresConfirmedIdentity: true` AND the call was originated
   * with `mode: 'required-with-confirm'` AND the called party has not
   * yet confirmed identity. Hosts surface a "Confirm" affordance
   * (modal, button, etc.) and call `sendIdentityConfirmed()` when the
   * user accepts.
   *
   * The runtime's gate enforcement (M4h-6d-2) holds dispatch until
   * either `visual.identity.confirmed` arrives OR the per-call
   * timeout expires — see `payload.timeoutMs`. The host SHOULD
   * surface remaining time so the user knows the call will end if
   * they don't confirm.
   *
   * Anti-pattern: do NOT inspect the failure-message text in
   * `onPrompt` to detect this state — this callback is the canonical
   * signal. The text in `onPrompt` is the audio prompt; this event
   * is the UI affordance trigger.
   */
  onIdentityRequired?: (payload: {
    sessionId: string
    nodeId: string
    failureMessage: string
    timeoutMs: number
  }) => void
}

const RETRYABLE_ERROR_CODES = new Set(['session-not-found'])
const PERMANENT_ERROR_CODES = new Set(['invalid-protocol', 'missing-token', 'session-expired'])

// Encoded-size budgets for the contextual-seed SIP headers added Jul 28 2026.
// SIP INVITEs travel over the SIP WebSocket here, but CamlinVoice re-reads
// them off the PJSIP channel where oversized packets risk UDP fragmentation
// on the Asterisk side — keep individual header values bounded. The greeting
// is one or two spoken sentences (AgentLink's are ~200 raw chars, ~400
// encoded); 1600 encoded chars leaves generous headroom without letting a
// runaway host blow up the INVITE. Display names are short by definition.
export const MAX_GREETING_HEADER_ENCODED_LENGTH = 1600
export const MAX_USER_DISPLAY_NAME_HEADER_ENCODED_LENGTH = 256

/**
 * URL-encode a header value, truncating the RAW string until the encoded
 * form fits `maxEncodedLength`. Truncation is done on the raw string (never
 * mid-escape) and guards against splitting a surrogate pair, which would
 * make `encodeURIComponent` throw on the lone high surrogate.
 *
 * Returns '' for non-strings / blank strings (caller omits the header).
 */
export function encodeHeaderValueWithLimit(value: unknown, maxEncodedLength: number): string {
  if (typeof value !== 'string') {
    return ''
  }
  let raw = value.trim()
  if (!raw) {
    return ''
  }

  let encoded = encodeURIComponent(raw)
  while (encoded.length > maxEncodedLength && raw.length > 0) {
    // Worst case every raw char encodes to 9 chars (astral via surrogate
    // pair = %XX%XX%XX + %XX%XX%XX ≈ 6 per half); shrinking proportionally
    // converges in a couple of iterations instead of one char at a time.
    const overshoot = encoded.length - maxEncodedLength
    const cut = Math.max(1, Math.ceil(overshoot / 3))
    raw = raw.slice(0, raw.length - cut)
    // Never end on a lone high surrogate — encodeURIComponent throws.
    const lastCode = raw.charCodeAt(raw.length - 1)
    if (lastCode >= 0xd800 && lastCode <= 0xdbff) {
      raw = raw.slice(0, -1)
    }
    raw = raw.trimEnd()
    encoded = encodeURIComponent(raw)
  }

  return encoded
}

type LiveModeError = Error & { code?: string }

const AZURE_BLENDSHAPE_NAMES = [
  'eyeBlinkLeft', 'eyeLookDownLeft', 'eyeLookInLeft', 'eyeLookOutLeft', 'eyeLookUpLeft',
  'eyeSquintLeft', 'eyeWideLeft', 'eyeBlinkRight', 'eyeLookDownRight', 'eyeLookInRight',
  'eyeLookOutRight', 'eyeLookUpRight', 'eyeSquintRight', 'eyeWideRight', 'jawForward',
  'jawLeft', 'jawRight', 'jawOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker',
  'mouthLeft', 'mouthRight', 'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft',
  'mouthFrownRight', 'mouthDimpleLeft', 'mouthDimpleRight', 'mouthStretchLeft',
  'mouthStretchRight', 'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower',
  'mouthShrugUpper', 'mouthPressLeft', 'mouthPressRight', 'mouthLowerDownLeft',
  'mouthLowerDownRight', 'mouthUpperUpLeft', 'mouthUpperUpRight', 'browDownLeft',
  'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight', 'cheekPuff',
  'cheekSquintLeft', 'cheekSquintRight', 'noseSneerLeft', 'noseSneerRight', 'tongueOut'
] as const

function insertHoldFramesForPauses(frames: LiveBlendshapeFrame[]): LiveBlendshapeFrame[] {
  const PAUSE_THRESHOLD_SECONDS = 0.05

  if (frames.length < 2) {
    return frames
  }

  const sortedFrames = [...frames].sort((left, right) => left.time - right.time)
  const result: LiveBlendshapeFrame[] = []

  sortedFrames.forEach((frame, index) => {
    if (index > 0) {
      const previousTime = result[result.length - 1].time
      const gap = frame.time - previousTime

      if (gap > PAUSE_THRESHOLD_SECONDS) {
        result.push({ time: previousTime + 0.016, values: {} })
        result.push({ time: frame.time - 0.016, values: {} })
      }
    }

    result.push(frame)
  })

  return result
}

function rawToFrames(rawBlendShapes: number[][], startTime: number, frameTimes?: number[]): LiveBlendshapeFrame[] {
  const timeStep = 1 / 60

  return rawBlendShapes.map((blendArray, index) => {
    const values: Record<string, number> = {}

    for (let blendIndex = 0; blendIndex < blendArray.length && blendIndex < AZURE_BLENDSHAPE_NAMES.length; blendIndex += 1) {
      const value = blendArray[blendIndex]
      if (Math.abs(value) > 0.01) {
        values[AZURE_BLENDSHAPE_NAMES[blendIndex]] = value
      }
    }

    return {
      time: typeof frameTimes?.[index] === 'number' ? frameTimes[index] : startTime + index * timeStep,
      values
    }
  })
}

function normalizeFrame(frame: unknown): LiveBlendshapeFrame | null {
  if (!frame || typeof frame !== 'object') {
    return null
  }

  const raw = frame as {
    time?: unknown
    values?: unknown
    blendshapes?: unknown
  }

  if (typeof raw.time !== 'number' || !Number.isFinite(raw.time)) {
    return null
  }

  const sourceValues =
    raw.values && typeof raw.values === 'object'
      ? raw.values as Record<string, unknown>
      : raw.blendshapes && typeof raw.blendshapes === 'object'
        ? raw.blendshapes as Record<string, unknown>
        : {}

  const values: Record<string, number> = {}
  for (const [key, value] of Object.entries(sourceValues)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      values[key] = value
    }
  }

  return {
    time: raw.time,
    values
  }
}

function normalizeFrames(frames: unknown[]): LiveBlendshapeFrame[] {
  return frames
    .map((frame) => normalizeFrame(frame))
    .filter((frame): frame is LiveBlendshapeFrame => frame !== null)
}

function estimateDurationMs(frames: LiveBlendshapeFrame[]): number {
  if (frames.length === 0) {
    return 0
  }

  return Math.round(Math.max(...frames.map((frame) => frame.time)) * 1000)
}

function normalizeValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function createToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `live-${crypto.randomUUID()}`
  }

  return `live-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function toWebSocketUrl(runtimeUrl: string): string {
  const baseUrl = runtimeUrl
    .replace(/\/$/, '')
    .replace(/\/runtime-api$/i, '')
  return `${baseUrl.replace(/^http/i, 'ws')}/visual-channel`
}

function createLiveModeError(code: string, message: string): LiveModeError {
  const error = new Error(message) as LiveModeError
  error.code = code
  return error
}

function normalizeContextualChatTurn(
  turn: AvatarContextualChatHistoryTurn | Record<string, unknown>
): AvatarContextualChatHistoryTurn | null {
  const role =
    turn.role === 'assistant'
      ? 'assistant'
      : turn.role === 'designer'
        ? 'designer'
        : turn.role === 'user'
          ? 'user'
          : null
  const content = typeof turn.content === 'string' ? turn.content.trim() : ''

  if (!role || !content) {
    return null
  }

  return { role, content }
}

export function createLiveSessionContext(
  context: Partial<AvatarSessionContext> = {},
  fallbackDiagramId?: string
): AvatarSessionContext {
  const sessionId = normalizeValue(context.sessionId) ?? normalizeValue(context.handoffToken) ?? createToken()
  const handoffToken = normalizeValue(context.handoffToken) ?? sessionId

  return {
    ...context,
    projectToken: normalizeValue(context.projectToken),
    diagramId: normalizeValue(context.diagramId) ?? normalizeValue(fallbackDiagramId),
    sessionId,
    handoffToken,
    channel: 'live',
    userId: normalizeValue(context.userId),
    tenantId: normalizeValue(context.tenantId),
    metadata: {
      ...(context.metadata ?? {}),
      liveMode: true
    }
  }
}

export function buildContextualChatMetadata(
  seed: AvatarContextualChatSeed = {},
  baseMetadata: Record<string, unknown> = {}
): Record<string, unknown> {
  const metadata: Record<string, unknown> = {
    ...baseMetadata,
    liveMode: true,
    liveSessionKind: 'contextual-chat',
    contextualLive: true
  }

  const assistantId = normalizeValue(seed.assistant?.id)
  const assistantName = normalizeValue(seed.assistant?.name)
  const assistantRole = normalizeValue(seed.assistant?.role)
  const assistantPrompt = normalizeValue(seed.assistant?.prompt)

  if (assistantId || assistantName || assistantRole || assistantPrompt) {
    metadata.assistantProfile = {
      ...(assistantId ? { id: assistantId } : {}),
      ...(assistantName ? { name: assistantName } : {}),
      ...(assistantRole ? { role: assistantRole } : {}),
      ...(assistantPrompt ? { prompt: assistantPrompt } : {})
    }

    if (assistantId) {
      metadata.specialistId = assistantId
    }
    if (assistantName) {
      metadata.specialistName = assistantName
    }
    if (assistantRole) {
      metadata.specialistRole = assistantRole
    }
    if (assistantPrompt) {
      metadata.specialistPrompt = assistantPrompt
    }
  }

  const contextSummary = normalizeValue(seed.contextSummary)
  if (contextSummary) {
    metadata.contextSummary = contextSummary
    metadata.contextSheet = contextSummary
  }

  const contextId = normalizeValue(seed.contextId)
  if (contextId) {
    metadata.contextId = contextId
    metadata.contextDiagramId = contextId
  }

  const interactionId = normalizeValue(seed.interactionId)
  if (interactionId) {
    metadata.interactionId = interactionId
    metadata.contextInteractionId = interactionId
  }

  const conversationHistory = Array.isArray(seed.conversationHistory)
    ? seed.conversationHistory
      .map((turn) => normalizeContextualChatTurn(turn))
      .filter((turn): turn is AvatarContextualChatHistoryTurn => turn !== null)
      .slice(-8)
    : []

  if (conversationHistory.length > 0) {
    metadata.conversationHistory = conversationHistory
  }

  // Host identifier — see AvatarContextualChatSeed.host docs for routing
  // semantics (controls whether the runtime calls /generate/host-chat or
  // /generate/designer-chat).
  const host = normalizeValue(seed.host)
  if (host) {
    metadata.host = host
  }

  // Optional spoken-greeting override. When set, the runtime uses this
  // string verbatim instead of synthesising one server-side — the only
  // way for tenant apps to fully control the greeting and avoid the
  // built-in template ("Hi, it's <name>. I'm here with your <product>
  // context…"). See ContextualLiveState.greeting in the runtime for
  // the consuming side.
  const greeting = normalizeValue(seed.greeting)
  if (greeting) {
    metadata.greeting = greeting
  }

  // Friendly user display name used by the runtime's fallback greeting
  // when no explicit `greeting` override is supplied. Lets hosts get
  // a "Hi <name>, it's <assistant>…" greeting for free without crafting
  // the entire string themselves.
  const userDisplayName = normalizeValue(seed.userDisplayName)
  if (userDisplayName) {
    metadata.userDisplayName = userDisplayName
  }

  // Tell the runtime to skip its own LLM call on `voice.transcript.final`
  // and let the host produce the spoken answer via `avatar:live:speak`.
  // See AvatarContextualChatSeed.suppressRuntimeAutoResponse and the
  // `useHostManagedVoiceTurn` hook for the consuming side.
  if (seed.suppressRuntimeAutoResponse === true) {
    metadata.suppressRuntimeAutoResponse = true
  }

  if (seed.metadata && typeof seed.metadata === 'object') {
    Object.assign(metadata, seed.metadata)
  }

  metadata.liveMode = true
  metadata.liveSessionKind = 'contextual-chat'
  metadata.contextualLive = true

  return metadata
}

export function createContextualChatSessionContext(
  context: Partial<AvatarSessionContext> = {},
  seed: AvatarContextualChatSeed = {}
): AvatarSessionContext {
  return createLiveSessionContext({
    ...context,
    metadata: buildContextualChatMetadata(
      seed,
      context.metadata && typeof context.metadata === 'object'
        ? context.metadata as Record<string, unknown>
        : {}
    )
  }, context.diagramId)
}

export function normalizeLiveSessionKind(value: unknown): AvatarLiveSessionKind | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  const normalized = value.trim().toLowerCase()
  if (normalized === 'contextual-chat' || normalized === 'contextual' || normalized === 'contextual-live') {
    return 'contextual-chat'
  }
  if (normalized === 'webrtc-call' || normalized === 'webrtc' || normalized === 'call') {
    return 'webrtc-call'
  }

  return undefined
}

export function resolveLiveSessionKindFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): AvatarLiveSessionKind | undefined {
  const explicitKind = normalizeLiveSessionKind(metadata?.liveSessionKind)
  if (explicitKind) {
    return explicitKind
  }

  if (metadata?.contextualLive === true || metadata?.contextualLive === 'true') {
    return 'contextual-chat'
  }

  return undefined
}

export function buildLiveSipHeaders(
  context: AvatarSessionContext,
  presentation?: {
    avatarModelId?: string | null
    voiceId?: string | null
  }
): string[] {
  const liveContext = createLiveSessionContext(context)
  const headers = [
    'X-Camlin-Mode: live',
    `X-Camlin-Session-Id: ${liveContext.sessionId}`,
    `X-Camlin-Handoff-Token: ${liveContext.handoffToken}`
  ]

  if (liveContext.projectToken) {
    headers.push(`X-Camlin-Project-Token: ${liveContext.projectToken}`)
  }

  if (liveContext.diagramId) {
    headers.push(`X-Camlin-Diagram-Id: ${liveContext.diagramId}`)
  }

  if (liveContext.userId) {
    headers.push(`X-Camlin-User-Id: ${liveContext.userId}`)
  }

  const metadata = liveContext.metadata && typeof liveContext.metadata === 'object'
    ? liveContext.metadata as Record<string, unknown>
    : {}
  const liveSessionKind = resolveLiveSessionKindFromMetadata(metadata) ?? 'webrtc-call'
  const specialistId = normalizeValue(
    typeof metadata.specialistId === 'string' ? metadata.specialistId : undefined
  )
  const encodeHeaderValue = (value: unknown): string => {
    if (typeof value !== 'string') {
      return ''
    }
    const trimmed = value.trim()
    return trimmed ? encodeURIComponent(trimmed) : ''
  }

  headers.push(`X-Camlin-Live-Session-Kind: ${liveSessionKind}`)

  if (liveSessionKind === 'contextual-chat') {
    headers.push('X-Camlin-Contextual-Live: true')
  }

  if (specialistId) {
    headers.push(`X-Camlin-Specialist-Id: ${specialistId}`)
  }
  const specialistName = encodeHeaderValue(metadata.specialistName)
  if (specialistName) {
    headers.push(`X-Camlin-Specialist-Name: ${specialistName}`)
  }
  const specialistRole = encodeHeaderValue(metadata.specialistRole)
  if (specialistRole) {
    headers.push(`X-Camlin-Specialist-Role: ${specialistRole}`)
  }
  const specialistPrompt = encodeHeaderValue(metadata.specialistPrompt)
  if (specialistPrompt) {
    headers.push(`X-Camlin-Specialist-Prompt: ${specialistPrompt}`)
  }
  const contextSheet = encodeHeaderValue(metadata.contextSheet ?? metadata.contextSummary)
  if (contextSheet) {
    headers.push(`X-Camlin-Context-Sheet: ${contextSheet}`)
  }

  // Contextual-greeting seed (Jul 28 2026). On real Voice/Video calls the
  // runtime session is minted from THIS INVITE (CamlinVoice session.start),
  // and the runtime speaks its greeting synchronously at session start —
  // before the visual channel links and before any visual.context.update
  // can land. These two headers are therefore the ONLY channel that can
  // deliver the verbatim greeting override and the friendly user name in
  // time for the first utterance. Same URL-encoding contract as
  // X-Camlin-Context-Sheet (CamlinVoice decodeHeaderValue side), with a
  // size budget so a runaway host can't bloat the INVITE.
  const greeting = encodeHeaderValueWithLimit(metadata.greeting, MAX_GREETING_HEADER_ENCODED_LENGTH)
  if (greeting) {
    headers.push(`X-Camlin-Greeting: ${greeting}`)
  }
  const userDisplayName = encodeHeaderValueWithLimit(
    metadata.userDisplayName,
    MAX_USER_DISPLAY_NAME_HEADER_ENCODED_LENGTH
  )
  if (userDisplayName) {
    headers.push(`X-Camlin-User-Display-Name: ${userDisplayName}`)
  }
  const contextDiagramId = encodeHeaderValue(metadata.contextDiagramId ?? metadata.contextId)
  if (contextDiagramId) {
    headers.push(`X-Camlin-Context-Diagram-Id: ${contextDiagramId}`)
  }
  const contextInteractionId = encodeHeaderValue(metadata.contextInteractionId ?? metadata.interactionId)
  if (contextInteractionId) {
    headers.push(`X-Camlin-Context-Interaction-Id: ${contextInteractionId}`)
  }
  const host = encodeHeaderValue(metadata.host)
  if (host) {
    headers.push(`X-Camlin-Context-Host: ${host}`)
  }

  // Tell CamlinVoice (and through it, the runtime's session.start) that this
  // tenant owns the response loop end-to-end. Without this header the SIP
  // path opens the runtime session WITHOUT `suppressRuntimeAutoResponse`,
  // and the very first `voice.transcript.final` races into
  // /generate/host-chat before the visual channel can push the flag via
  // visual.context.update — exactly the regression Decision Memory and
  // Tradie Pro hit after shipping the SDK 2.1.5 host-managed voice mode.
  // The runtime side is already fixed (3d90c817); this header closes the
  // SIP gap so the flag is set BEFORE the first transcript arrives.
  if (metadata.suppressRuntimeAutoResponse === true) {
    headers.push('X-Camlin-Suppress-Runtime-Auto-Response: true')
  }

  const avatarModelId = typeof presentation?.avatarModelId === 'string'
    ? presentation.avatarModelId.trim()
    : ''
  if (avatarModelId) {
    headers.push(`X-Camlin-Avatar-Model-Id: ${avatarModelId}`)
  }

  // Per-specialist voice override. Without this header the runtime falls
  // back to AVATAR_MODEL_VOICES[avatarModelId] and every specialist sounds
  // the same (or whatever the model default is). The header is honoured by
  // CamlinVoice's CallHandler.js (channel var CAMLIN_VOICE_ID) which
  // forwards it as part of the session.start context to runtime-service,
  // which then injects it into ttsConfig.voice on every turn output.
  const voiceId = typeof presentation?.voiceId === 'string'
    ? presentation.voiceId.trim()
    : ''
  if (voiceId) {
    headers.push(`X-Camlin-Voice-Id: ${voiceId}`)
  }

  return headers
}

export function isRetryableLiveSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const code = typeof (error as LiveModeError).code === 'string'
    ? String((error as LiveModeError).code)
    : ''

  return RETRYABLE_ERROR_CODES.has(code)
}

export class LiveModeClient {
  private readonly config: LiveModeClientConfig
  private socket: WebSocket | null = null
  private reconnectAttempts = 0
  private readonly maxReconnectAttempts = 6
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private shouldReconnect = true
  private linked = false
  private readyRequested = false
  private playbackActive = false
  private awaitingPlaybackStart = false
  private pendingBlendshapes: LiveBlendshapeFrame[] | null = null
  // True from the first visual.blendshapeChunk of a turn until its isFinal
  // chunk arrives. While open, `pendingBlendshapes` is the TURN accumulator
  // and must survive a dispatch: for a long reply the runtime streams chunks
  // for as long as Azure is still synthesizing (often well past 3 s), and any
  // dispatch that also cleared the buffer used to orphan every later chunk —
  // the renderer got only the first few sentences' frames, the speaking
  // window was sized to that fragment, and auto-listen then stopped the audio
  // mid-sentence. That is the "companion only reads the first few sentences"
  // defect (Keith, 7 Aug 2026).
  private blendshapeStreamOpen = false
  // Progressive dispatch (2.12.0): stage bookkeeping for the current turn.
  private progressiveStage2Done = false
  private playbackFallbackTimer: ReturnType<typeof setTimeout> | null = null
  private lastHeartbeatTime = 0
  private rttSamples: number[] = []
  private rttAverage = 50
  private readonly rttSampleSize = 5
  private initialResolver: (() => void) | null = null
  private initialRejecter: ((error: LiveModeError) => void) | null = null
  private initialSettled = false
  private lastErrorCode: string | null = null
  // Pending context update to flush once `visual.linked` arrives. Hosts
  // (AgentLink, Decision Memory, builder embeds) call `sendSessionContext`
  // as soon as `liveModeActive` flips true, but in voice-call mode the
  // visual channel is still mid-handshake at that moment, so the update
  // would otherwise be silently dropped and the runtime would never receive
  // `specialistPrompt` / `specialistName` / `contextSheet` etc. — leaving
  // contextual-chat sessions without an LLM persona and unable to reply to
  // user input.
  private pendingSessionContext: Record<string, unknown> | null = null
  // Latest context the host asked us to deliver, even after linking. Kept
  // so a reconnect re-syncs the runtime side without the host having to
  // re-issue the call.
  private lastSessionContext: Record<string, unknown> | null = null
  // User inputs sent before `visual.linked` (6 Aug 2026, Companion v4 pass).
  // `sendInput` used to silently drop these — a visitor typing during the
  // enable-sound session restart lost their message and only saw the greeting
  // replay (recorded in evidence/companion-packaging-aug6). Same flush-on-link
  // pattern as pendingSessionContext above.
  private pendingInputs: Array<{ value: string; metadata?: Record<string, unknown> }> = []

  constructor(config: LiveModeClientConfig) {
    this.config = config
  }

  connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN && this.linked) {
      return Promise.resolve()
    }

    this.shouldReconnect = true
    this.initialSettled = false
    this.initialResolver = null
    this.initialRejecter = null

    return new Promise((resolve, reject) => {
      this.initialResolver = resolve
      this.initialRejecter = reject
      this.openSocket()
    })
  }

  disconnect(): void {
    console.log('[LiveMode] disconnect requested', {
      handoffToken: this.config.handoffToken,
      wasLinked: this.linked,
      socketReadyState: this.socket?.readyState
    })
    this.shouldReconnect = false
    this.clearReconnectTimer()
    this.stopHeartbeat()
    this.clearPlaybackFallback()
    this.readyRequested = false
    this.pendingBlendshapes = null
    this.blendshapeStreamOpen = false
    this.awaitingPlaybackStart = false
    this.playbackActive = false
    this.linked = false

    if (this.socket) {
      this.socket.close()
      this.socket = null
    }

    this.config.onStateChange?.('disconnected')
  }

  sendReady(): void {
    this.readyRequested = true

    if (!this.linked) {
      // Defer until `visual.linked` arrives — see `handleMessage` case
      // 'visual.linked' below, which checks `readyRequested` and calls
      // `sendReady()` again. Used to silently no-op here, which made
      // "the avatar looks ready but the bot isn't talking back" symptoms
      // impossible to diagnose without source-level breakpoints. Locked
      // down Apr-30 2026 (live-mode reliability fix).
      console.log('[LiveMode] sendReady deferred — channel not linked yet, will fire on visual.linked', {
        handoffToken: this.config.handoffToken,
        socketReadyState: this.socket?.readyState
      })
      return
    }

    console.log('[LiveMode] sending visual.ready', {
      handoffToken: this.config.handoffToken
    })
    this.send({
      type: 'visual.ready',
      payload: {
        sessionId: this.config.handoffToken,
        timestamp: Date.now()
      }
    })
  }

  /**
   * True only when the visual channel is genuinely usable RIGHT NOW:
   * linked handshake completed AND the underlying socket is still open.
   * Hosts should check this on user actions (send/mic) — a tab that slept
   * past session expiry can hold `linked === true` with a dead socket, and
   * `send()` below drops silently in that state (Companion v8 item 2,
   * 7 Aug 2026: "mic turn recognized, sent, then nothing").
   */
  isLinked(): boolean {
    return this.linked && this.socket?.readyState === WebSocket.OPEN
  }

  sendInput(value: string, metadata?: Record<string, unknown>): void {
    if (!this.linked) {
      // Queue for delivery on `visual.linked` instead of dropping. A user
      // message typed while the channel is mid-handshake (e.g. during the
      // Companion's enable-sound session restart) must survive the wait.
      console.log('[LiveMode] sendInput deferred — channel not linked yet, queued for visual.linked', {
        handoffToken: this.config.handoffToken,
        queuedInputs: this.pendingInputs.length + 1
      })
      this.pendingInputs.push({ value, metadata })
      return
    }

    this.send({
      type: 'visual.input',
      payload: {
        value,
        metadata
      }
    })
  }

  private flushPendingInputs(): void {
    if (!this.linked || this.pendingInputs.length === 0) {
      return
    }
    const queued = this.pendingInputs
    this.pendingInputs = []
    console.log('[LiveMode] flushing', queued.length, 'queued input(s) after visual.linked')
    for (const { value, metadata } of queued) {
      this.send({
        type: 'visual.input',
        payload: {
          value,
          metadata
        }
      })
    }
  }

  sendSpeak(text: string, voiceId?: string): void {
    if (!this.linked || !text?.trim()) {
      return
    }

    this.send({
      type: 'visual.speak',
      payload: {
        text: text.trim(),
        ...(voiceId ? { voiceId } : {})
      }
    })
  }

  /**
   * Stop the avatar's in-flight utterance AT THE SOURCE (Aug 29 2026 —
   * Decision Memory live calls: the presenter needs to cut off a long
   * answer without muting or ending the call).
   *
   * Sends `visual.playback.stop`; the runtime forwards it to the voice
   * leg as the same `visual.bargein` the Visual IVR input path uses, so
   * CamlinVoice halts the ARI playback and the runtime broadcasts
   * `visual.playback.stopped` back to this client (handled above:
   * blendshapes cleared, onSpeaking(false), ASR re-arms server-side).
   *
   * Deliberately NOT `visual.input`: this verb must never feed the
   * session an input — reusing the input path would make the runtime
   * author and speak an answer to the stop itself.
   */
  sendStopSpeaking(): void {
    if (!this.linked) {
      return
    }
    console.log('[LiveMode] sending visual.playback.stop', {
      handoffToken: this.config.handoffToken
    })
    this.send({
      type: 'visual.playback.stop',
      payload: { timestamp: Date.now() }
    })
  }

  /**
   * Slice M4h-6d-4 (May 12 2026) — Visual IVR identity gate release.
   *
   * Sends `visual.identity.confirmed` to the runtime, which calls
   * `releaseIdentityGate(sessionId, 'visual-channel-confirm')` and
   * resumes dispatch from the held node.
   *
   * Idempotent on the runtime side (additional events after the
   * first are no-ops). Safe to wire to a button that may double-fire
   * from a debounce-less click handler.
   *
   * Anti-pattern: do NOT call this on page load "just in case". The
   * release is global — it flips `identityConfirmed: true` for the
   * whole session, which means subsequent gated nodes also pass. The
   * call MUST be a deliberate user action (button click) so the
   * security boundary stays meaningful.
   */
  sendIdentityConfirmed(): void {
    if (!this.linked) {
      console.warn('[LiveMode] sendIdentityConfirmed called before channel linked — ignoring', {
        handoffToken: this.config.handoffToken
      })
      return
    }
    console.log('[LiveMode] sending visual.identity.confirmed', {
      handoffToken: this.config.handoffToken
    })
    this.send({
      type: 'visual.identity.confirmed'
    })
  }

  sendSessionContext(context: Record<string, unknown>): void {
    if (!context || typeof context !== 'object') {
      return
    }

    this.lastSessionContext = context

    if (!this.linked) {
      // Queue for replay when `visual.linked` arrives. This is the common
      // case for voice-call mode where hosts call `sendSessionContext`
      // before the visual channel handshake completes.
      this.pendingSessionContext = context
      return
    }

    this.send({
      type: 'visual.context.update',
      payload: {
        sessionId: this.config.handoffToken,
        context
      }
    })
  }

  private flushPendingSessionContext(): void {
    const context = this.pendingSessionContext ?? this.lastSessionContext
    if (!context) {
      return
    }
    this.pendingSessionContext = null
    if (!this.linked) {
      return
    }
    this.send({
      type: 'visual.context.update',
      payload: {
        sessionId: this.config.handoffToken,
        context
      }
    })
  }

  sendAnimationReady(frameCount: number, durationMs: number): void {
    this.send({
      type: 'visual.animation.ready',
      payload: {
        frameCount,
        durationMs,
        timestamp: Date.now(),
        measuredRttMs: this.rttAverage,
        rttSamples: this.rttSamples.length
      }
    })
  }

  private openSocket(): void {
    this.clearReconnectTimer()
    this.config.onStateChange?.('connecting')

    // Diagnostics — every state change in this socket lifecycle MUST be
    // observable from the browser console. The whole live-mode pipeline
    // used to be silent here (no logs at open/close/error/visual.linked)
    // which made every regression a multi-day "looks like it should work,
    // why doesn't it" debugging session. The standardised `[LiveMode]`
    // prefix matches existing consumer-side logs (`useLiveProvisionIdentity`,
    // `useLiveVisualChannel`) so the lifecycle reads as a single thread
    // in the console. Locked down Apr-30 2026 (live-mode reliability fix).
    const wsUrl = toWebSocketUrl(this.config.runtimeUrl)
    console.log('[LiveMode] opening visual channel websocket', {
      wsUrl,
      handoffToken: this.config.handoffToken,
      reconnectAttempt: this.reconnectAttempts
    })

    const socket = new WebSocket(wsUrl)
    this.socket = socket

    socket.onopen = () => {
      // Contextual-greeting seed (Jul 28 2026): if the host already handed
      // us session context (greeting / conversationHistory / specialist
      // persona via sendSessionContext), ship it INSIDE visual.link — the
      // very first message on the channel — instead of only replaying it as
      // a post-link visual.context.update. Post-link delivery is too late
      // to influence the runtime's first utterance; in-link delivery lets
      // the WS path beat the greeting. The pending context is deliberately
      // NOT cleared here: the flush-on-linked backstop still re-sends it as
      // visual.context.update (the runtime's merge is idempotent), which
      // keeps older runtimes that ignore `context` on visual.link working.
      const seedContext = this.pendingSessionContext ?? this.lastSessionContext
      console.log('[LiveMode] websocket open — sending visual.link', {
        handoffToken: this.config.handoffToken,
        withSeedContext: Boolean(seedContext)
      })
      this.send({
        type: 'visual.link',
        payload: {
          handoffToken: this.config.handoffToken,
          ...(seedContext ? { context: seedContext } : {})
        }
      })
    }

    socket.onmessage = (event) => {
      this.handleMessage(event.data)
    }

    socket.onerror = (event) => {
      console.warn('[LiveMode] websocket error', {
        handoffToken: this.config.handoffToken,
        readyState: socket.readyState,
        type: (event as Event)?.type,
        linked: this.linked
      })
      if (!this.initialSettled && !this.linked) {
        this.rejectInitial(createLiveModeError(
          this.lastErrorCode ?? 'connection-failed',
          'Unable to connect live visual channel'
        ))
      }
    }

    socket.onclose = (event) => {
      const wasLinked = this.linked
      console.log('[LiveMode] websocket closed', {
        handoffToken: this.config.handoffToken,
        code: event?.code,
        reason: event?.reason || null,
        wasLinked,
        wasClean: event?.wasClean,
        shouldReconnect: this.shouldReconnect
      })
      this.stopHeartbeat()
      this.clearPlaybackFallback()
      this.socket = null
      this.linked = false
      this.playbackActive = false
      this.awaitingPlaybackStart = false
      this.pendingBlendshapes = null
      this.blendshapeStreamOpen = false

      if (!this.initialSettled && !wasLinked) {
        this.rejectInitial(createLiveModeError(
          this.lastErrorCode ?? 'connection-closed',
          'Live visual channel closed before linking'
        ))
      }

      if (this.shouldReconnect && wasLinked && !this.isPermanentError()) {
        this.scheduleReconnect()
        return
      }

      if (!this.shouldReconnect) {
        this.config.onStateChange?.('disconnected')
      }
    }
  }

  private handleMessage(raw: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (typeof raw !== 'string') {
      return
    }

    let message: { type?: string; payload?: Record<string, unknown> }

    try {
      message = JSON.parse(raw)
    } catch {
      return
    }

    const payload = (message?.payload ?? {}) as Record<string, unknown>

    switch (message?.type) {
      case 'visual.linked':
        console.log('[LiveMode] visual.linked received — channel ready', {
          handoffToken: this.config.handoffToken,
          sessionId: payload.sessionId,
          diagramId: payload.diagramId,
          readyAlreadyRequested: this.readyRequested
        })
        this.lastErrorCode = null
        this.linked = true
        this.reconnectAttempts = 0
        this.config.onStateChange?.('linked')
        this.startHeartbeat()
        this.send({
          type: 'visual.connected',
          payload: {
            sessionId: payload.sessionId ?? this.config.handoffToken,
            timestamp: Date.now()
          }
        })
        // Flush any queued `visual.context.update` (e.g. specialist prompt
        // for contextual-chat sessions) now that the runtime can route it
        // to the linked session. Voice-call hosts rely on this — they call
        // sendSessionContext before the handshake completes.
        this.flushPendingSessionContext()
        // Context first, then any user inputs queued while unlinked — the
        // runtime needs the persona/context in place before routing input.
        this.flushPendingInputs()
        if (this.readyRequested) {
          this.sendReady()
        }
        this.config.onLinked?.(
          String(payload.sessionId ?? this.config.handoffToken),
          normalizeValue(payload.diagramId),
          normalizeValue(payload.avatarModelId),
          payload.avatarConfig && typeof payload.avatarConfig === 'object'
            ? payload.avatarConfig as Record<string, unknown>
            : undefined
        )
        this.resolveInitial()
        break
      case 'visual.prompt':
        this.clearPlaybackFallback()
        this.playbackActive = false
        this.awaitingPlaybackStart = false
        this.pendingBlendshapes = null
        this.blendshapeStreamOpen = false
        this.config.onPrompt?.(payload as LivePrompt)
        if (typeof payload.listening === 'boolean') {
          this.config.onListening?.(payload.listening)
        }
        {
          const visual = payload.visual as Record<string, unknown> | undefined
          const gesture = visual?.gesture as string | undefined
          if (gesture && gesture !== 'none' && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('avatar:gesture', {
              detail: { gesture, text: payload.text ?? '' }
            }))
          }
        }
        break
      case 'visual.listening':
        this.config.onListening?.(Boolean(payload.active))
        break
      case 'visual.thinking':
        this.config.onThinking?.(Boolean(payload.thinking ?? payload.active))
        break
      case 'visual.transcript':
        this.config.onTranscript?.(String(payload.text ?? ''), Boolean(payload.isFinal))
        break
      case 'visual.sentiment':
        this.config.onSentiment?.({
          label: String(payload.sentiment ?? payload.label ?? 'neutral'),
          score: Number(payload.confidence ?? payload.score ?? 0)
        })
        break
      case 'visual.identity.required':
        // Slice M4h-6d-4 (May 12 2026) — runtime hit a node tagged
        // `requiresConfirmedIdentity: true` AND identityRequired is
        // set. Surface the UI affordance via the host callback.
        // The runtime ALSO sent a `session.turn.output` with the
        // failure-message audio prompt — the two events are
        // companion signals (audio + UI) for the same gate fire.
        console.log('[LiveMode] visual.identity.required received', {
          handoffToken: this.config.handoffToken,
          sessionId: payload.sessionId,
          nodeId: payload.nodeId,
          timeoutMs: payload.timeoutMs
        })
        this.config.onIdentityRequired?.({
          sessionId: String(payload.sessionId ?? this.config.handoffToken),
          nodeId: String(payload.nodeId ?? ''),
          failureMessage: String(payload.failureMessage ?? ''),
          timeoutMs: Number(payload.timeoutMs ?? 60_000)
        })
        break
      case 'visual.escalation':
        // Sentiment-decline trend crossed the threshold (sprint 20.5).
        // Runtime emits this from apps/runtime-service/src/server.ts; the
        // host should switch to a "connecting you to a person" UI or
        // route to a human agent. Restored after a regression where the
        // canonical client silently dropped this message at default:.
        this.config.onEscalation?.({
          triggered: Boolean(payload.triggered),
          reason: String(payload.reason ?? ''),
          trend: String(payload.trend ?? '')
        })
        break
      case 'visual.node_changed':
        // Flow advanced to a new node. Used by debug overlays + analytics
        // hooks. Runtime emits via apps/runtime-service/src/server.ts;
        // restored after a regression where the canonical client silently
        // dropped this message.
        this.config.onNodeChanged?.(
          String(payload.nodeId ?? ''),
          String(payload.type ?? payload.nodeType ?? ''),
          String(payload.title ?? payload.nodeTitle ?? '')
        )
        break
      case 'visual.switchAvatar': {
        const avatarModelId = normalizeValue(payload.avatarModelId)
        if (avatarModelId) {
          this.config.onSwitchAvatar?.(
            avatarModelId,
            normalizeValue(payload.voiceId),
            normalizeValue(payload.transitionMessage)
          )
        }
        break
      }
      case 'visual.blendshapes': {
        const frames = Array.isArray(payload.frames)
          ? normalizeFrames(payload.frames)
          : []

        if (!frames.length) {
          break
        }

        const framesWithHolds = insertHoldFramesForPauses(frames)

        if (this.playbackActive) {
          this.config.onBlendshapes?.(framesWithHolds)
        } else {
          if (this.pendingBlendshapes) {
            this.pendingBlendshapes.push(...framesWithHolds)
          } else {
            this.pendingBlendshapes = [...framesWithHolds]
            this.awaitingPlaybackStart = true
            this.startPlaybackFallback(2000)
          }

          this.sendAnimationReady(
            this.pendingBlendshapes.length,
            estimateDurationMs(this.pendingBlendshapes)
          )
        }
        break
      }
      case 'visual.blendshapeChunk': {
        const chunkIndex = typeof payload.chunkIndex === 'number' ? payload.chunkIndex : 0
        const rawBlendShapes = Array.isArray(payload.rawBlendShapes) ? payload.rawBlendShapes as number[][] : null
        const frameTimes = Array.isArray(payload.frameTimes)
          ? payload.frameTimes.filter((value): value is number => typeof value === 'number')
          : undefined
        const chunkFrames = rawBlendShapes
          ? rawToFrames(rawBlendShapes, Number(payload.startTime ?? 0), frameTimes)
          : Array.isArray(payload.frames)
            ? normalizeFrames(payload.frames)
            : []

        if (chunkIndex === 0) {
          this.pendingBlendshapes = [...chunkFrames]
          this.blendshapeStreamOpen = payload.isFinal !== true
          this.awaitingPlaybackStart = !this.playbackActive
          this.progressiveStage2Done = false
        } else if (this.pendingBlendshapes) {
          this.pendingBlendshapes.push(...chunkFrames)
        } else {
          this.pendingBlendshapes = [...chunkFrames]
          this.awaitingPlaybackStart = !this.playbackActive
          this.progressiveStage2Done = false
        }

        // Progressive dispatch (2.12.0, browser-only chat). The frames are in
        // hand long before Azure finishes the reply, and the consumer
        // audio-gates every dispatch (useLivePromptPlayback buffers until the
        // <audio> element is playing), so hand them over as they arrive:
        //   stage 1  chunk 0 — the mouth can start with the first words;
        //   stage 2  one superset once >= 8 s of speech is buffered;
        //   stage 3  the complete turn on isFinal, flagged `deferToPause` so
        //            the consumer flushes it at a speech pause.
        // The accumulator is never cleared while the stream is open — that
        // is the invariant the 7 Aug 2026 truncation fix (612728a) rests on.
        if (this.config.progressiveDispatch === true) {
          if (payload.isFinal === true) {
            this.blendshapeStreamOpen = false
            this.awaitingPlaybackStart = false
            this.clearPlaybackFallback()
            const frames = this.pendingBlendshapes ? insertHoldFramesForPauses(this.pendingBlendshapes) : []
            this.pendingBlendshapes = null
            this.playbackActive = true
            if (frames.length > 0) {
              this.config.onBlendshapes?.(frames, { stage: 'final', final: true, deferToPause: chunkIndex > 0 })
            }
            break
          }
          if (chunkIndex === 0) {
            this.dispatchProgressive('chunk0')
          } else if (
            !this.progressiveStage2Done &&
            this.pendingBlendshapes &&
            estimateDurationMs(this.pendingBlendshapes) >= PROGRESSIVE_STAGE2_MIN_BUFFERED_MS
          ) {
            this.progressiveStage2Done = true
            this.dispatchProgressive('stage2')
          }
          break
        }

        // The fallback is a STALL detector, re-armed on every chunk — it must
        // fire 3 s after the stream goes quiet, not 3 s after the stream
        // STARTED. A long reply streams chunks for the whole Azure synthesis
        // (regularly longer than 3 s), and the old first-chunk deadline fired
        // mid-stream, dispatching the first few sentences' frames as if they
        // were the whole turn.
        if (this.awaitingPlaybackStart) {
          this.startPlaybackFallback(3000)
        }

        if (payload.isFinal === true) {
          this.blendshapeStreamOpen = false

          if (this.pendingBlendshapes && this.pendingBlendshapes.length > 0) {
            this.pendingBlendshapes = insertHoldFramesForPauses(this.pendingBlendshapes)

            if (this.playbackActive) {
              // Playback already started (playback.started or the stall
              // fallback dispatched a partial set). The buffer still holds
              // the FULL turn — frames are absolute-timed and the renderer
              // follows the audio clock, so re-dispatching the complete set
              // upgrades the running animation seamlessly.
              this.dispatchPendingBlendshapes()
            } else {
              this.sendAnimationReady(
                this.pendingBlendshapes.length,
                estimateDurationMs(this.pendingBlendshapes)
              )
            }
          }
        }
        break
      }
      case 'visual.playback.started':
        this.playbackActive = true
        this.awaitingPlaybackStart = false
        this.clearPlaybackFallback()
        this.config.onListening?.(false)
        this.config.onThinking?.(false)
        this.config.onSpeaking?.(true)
        this.dispatchPendingBlendshapes()
        break
      case 'visual.playback.countdown': {
        const delayMs = typeof payload.delayMs === 'number' ? Math.max(0, payload.delayMs) : 0

        if (!this.pendingBlendshapes || this.pendingBlendshapes.length === 0) {
          break
        }

        if (this.config.onPlaybackCountdown) {
          const frames = [...this.pendingBlendshapes]
          this.pendingBlendshapes = null
          this.blendshapeStreamOpen = false
          this.awaitingPlaybackStart = false
          this.clearPlaybackFallback()
          this.playbackActive = true
          this.config.onPlaybackCountdown(delayMs, frames)
        } else {
          window.setTimeout(() => {
            if (this.pendingBlendshapes && this.pendingBlendshapes.length > 0) {
              this.config.onBlendshapes?.(this.pendingBlendshapes)
            }
            this.pendingBlendshapes = null
            this.blendshapeStreamOpen = false
            this.awaitingPlaybackStart = false
            this.clearPlaybackFallback()
            this.playbackActive = true
          }, delayMs)
        }
        break
      }
      case 'visual.playback.stopped':
        this.clearPlaybackFallback()
        this.playbackActive = false
        this.awaitingPlaybackStart = false
        this.pendingBlendshapes = null
        this.blendshapeStreamOpen = false
        this.config.onSpeaking?.(false)
        break
      case 'visual.audio.position':
        this.config.onAudioPosition?.({
          currentTime: Number(payload.currentTime ?? 0),
          duration: Number(payload.duration ?? 0),
          isPlaying: Boolean(payload.isPlaying),
          timestamp: typeof payload.timestamp === 'number' ? payload.timestamp : undefined
        })
        break
      case 'visual.heartbeat.ack':
        if (this.lastHeartbeatTime > 0) {
          const rtt = performance.now() - this.lastHeartbeatTime
          this.lastHeartbeatTime = 0
          this.rttSamples.push(rtt)

          if (this.rttSamples.length > this.rttSampleSize) {
            this.rttSamples.shift()
          }

          this.rttAverage = Math.round(
            this.rttSamples.reduce((sum, sample) => sum + sample, 0) / this.rttSamples.length
          )
        }
        break
      case 'visual.ready.ack':
        break
      case 'visual.call.ended':
        // The runtime has authoritatively ended this session. The server
        // closes the socket right after — without this flag the onclose
        // handler would schedule up to 6 futile re-links into a session
        // that is gone (each bouncing off `session-not-found`), churning
        // the network for ~20s after every remote hangup.
        this.shouldReconnect = false
        this.clearPlaybackFallback()
        this.playbackActive = false
        this.awaitingPlaybackStart = false
        this.pendingBlendshapes = null
        this.blendshapeStreamOpen = false
        this.config.onSpeaking?.(false)
        this.config.onListening?.(false)
        this.config.onThinking?.(false)
        this.config.onCallEnded?.(String(payload.reason ?? 'ended'))
        break
      case 'visual.error': {
        const code = String(payload.code ?? 'visual-error')
        const messageText = String(payload.message ?? 'Visual channel error')
        // Surface the runtime's rejection reason in the console. Without
        // this the host operator sees `[LiveMode] websocket closed` 50ms
        // after `visual.link` was sent and has no idea WHY — the
        // diagnostic for "WebSocket flapping" regressions is buried in
        // runtime CloudWatch logs. Locked down May-1 2026.
        console.warn('[LiveMode] visual.error received from runtime', {
          handoffToken: this.config.handoffToken,
          code,
          message: messageText
        })
        this.lastErrorCode = code
        this.config.onError?.(messageText)
        this.config.onStateChange?.('error')
        this.rejectInitial(createLiveModeError(code, messageText))
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.close()
        }
        break
      }
      default:
        break
    }
  }

  private dispatchPendingBlendshapes(): void {
    if (this.pendingBlendshapes && this.pendingBlendshapes.length > 0) {
      this.config.onBlendshapes?.(this.pendingBlendshapes)
    }
    this.awaitingPlaybackStart = false
    this.clearPlaybackFallback()
    // While a chunk stream is still open the buffer is the turn accumulator:
    // keep it so later chunks extend it and the isFinal dispatch hands the
    // renderer the complete turn. Clearing here is what used to orphan every
    // chunk that arrived after playback.started on a long reply.
    if (!this.blendshapeStreamOpen) {
      this.pendingBlendshapes = null
    }
  }

  /**
   * Progressive dispatch (2.12.0): hand the consumer what has arrived so far
   * WITHOUT clearing the accumulator — the stream is still open and later
   * chunks extend the same buffer until isFinal delivers the complete turn.
   */
  private dispatchProgressive(stage: 'chunk0' | 'stage2'): void {
    if (!this.pendingBlendshapes || this.pendingBlendshapes.length === 0) {
      return
    }
    this.awaitingPlaybackStart = false
    this.clearPlaybackFallback()
    this.playbackActive = true
    console.log('[LiveMode] progressive viseme dispatch', {
      stage,
      frameCount: this.pendingBlendshapes.length,
      bufferedMs: estimateDurationMs(this.pendingBlendshapes)
    })
    // Copy: the consumer may keep the array while we keep appending.
    this.config.onBlendshapes?.([...this.pendingBlendshapes], { stage, final: false })
  }

  private startPlaybackFallback(delayMs: number): void {
    this.clearPlaybackFallback()
    this.playbackFallbackTimer = window.setTimeout(() => {
      this.playbackFallbackTimer = null
      if (!this.awaitingPlaybackStart || !this.pendingBlendshapes || this.pendingBlendshapes.length === 0) {
        return
      }

      this.config.onBlendshapes?.(this.pendingBlendshapes)
      // Same accumulator rule as dispatchPendingBlendshapes: a stalled-but-
      // still-open stream keeps its buffer so a late tail (slow Azure
      // synthesis, network hiccup) still reaches the renderer as a complete
      // turn via the isFinal re-dispatch.
      if (!this.blendshapeStreamOpen) {
        this.pendingBlendshapes = null
      }
      this.awaitingPlaybackStart = false
      this.playbackActive = true
    }, delayMs)
  }

  private clearPlaybackFallback(): void {
    if (!this.playbackFallbackTimer) {
      return
    }

    clearTimeout(this.playbackFallbackTimer)
    this.playbackFallbackTimer = null
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.lastHeartbeatTime = performance.now()
        this.send({
          type: 'visual.heartbeat',
          payload: { timestamp: Date.now() }
        })
      }
    }, 30000)

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.lastHeartbeatTime = performance.now()
      this.send({
        type: 'visual.heartbeat',
        payload: { timestamp: Date.now() }
      })
    }
  }

  private stopHeartbeat(): void {
    if (!this.heartbeatInterval) {
      return
    }

    clearInterval(this.heartbeatInterval)
    this.heartbeatInterval = null
  }

  private send(message: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return
    }

    this.socket.send(JSON.stringify(message))
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // terminal: no further reconnects will be attempted — the consumer
      // must treat the session as over (see LiveModeClientConfig.onError).
      this.config.onError?.('Live visual channel disconnected', true)
      this.config.onStateChange?.('error')
      return
    }

    this.reconnectAttempts += 1
    const delayMs = Math.min(1000 * this.reconnectAttempts, 5000)
    this.reconnectTimer = setTimeout(() => {
      if (this.shouldReconnect) {
        this.openSocket()
      }
    }, delayMs)
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return
    }

    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }

  private resolveInitial(): void {
    if (this.initialSettled) {
      return
    }

    this.initialSettled = true
    this.initialResolver?.()
    this.initialResolver = null
    this.initialRejecter = null
  }

  private rejectInitial(error: LiveModeError): void {
    if (this.initialSettled) {
      return
    }

    this.initialSettled = true
    this.initialRejecter?.(error)
    this.initialResolver = null
    this.initialRejecter = null
  }

  private isPermanentError(): boolean {
    return this.lastErrorCode !== null && PERMANENT_ERROR_CODES.has(this.lastErrorCode)
  }
}
